/**
 * LoginPage Component
 *
 * The main login page for Auth.HireNow.com.
 * Provides multiple sign-in options:
 * - Email/Password (primary, shown by default)
 * - Microsoft (Entra ID) for enterprise users (secondary)
 * - Sign up for new users
 * - Forgot password flow
 */

import { useState, useCallback, type FormEvent } from 'react';
import { useAuth } from '../auth';
import { sendPasswordResetEmail } from '../auth/firebase';
import { Loading } from './Loading';

/**
 * LoginPage Component
 *
 * Displays the HireNow branding and sign-in options.
 * Email/password form is shown by default with Microsoft as an alternative.
 */
export function LoginPage() {
  const { login, register, switchProvider, isLoading, error, clearError } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Handle Microsoft sign-in button click
   */
  const handleMicrosoftSignIn = useCallback(async () => {
    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      switchProvider('firebase-microsoft');
      await login({ interaction: 'popup' });
    } catch (err) {
      console.error('[LoginPage] Microsoft sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, switchProvider, clearError]);

  /**
   * Handle email/password sign-in form submission
   */
  const handleEmailSignIn = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      switchProvider('firebase-email');
      await login({ email, password });
    } catch (err) {
      console.error('[LoginPage] Email sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, switchProvider, clearError, email, password]);

  /**
   * Handle sign-up form submission
   */
  const handleSignUp = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      await register(email, password);
    } catch (err) {
      console.error('[LoginPage] Sign-up failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [register, clearError, email, password]);

  /**
   * Handle forgot password form submission
   */
  const handleForgotPassword = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      await sendPasswordResetEmail(email);
      setSuccessMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      console.error('[LoginPage] Password reset failed:', err);
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'auth/user-not-found') {
        setLocalError('No account found with this email address');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setLocalError('Invalid email address');
      } else {
        setLocalError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  }, [clearError, email]);

  /**
   * Switch between modes
   */
  const switchMode = useCallback((newMode: 'signin' | 'signup' | 'forgot') => {
    setMode(newMode);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);
    setPassword('');
  }, [clearError]);

  // Show loading state during initial auth check
  if (isLoading && !isSigningIn) {
    return <Loading fullScreen message="Checking authentication..." />;
  }

  const isSignUp = mode === 'signup';
  const isForgot = mode === 'forgot';

  // Get title and subtitle based on mode
  const getTitle = () => {
    if (isForgot) return 'Reset Password';
    if (isSignUp) return 'Create Account';
    return 'Welcome';
  };

  const getSubtitle = () => {
    if (isForgot) return 'Enter your email to receive a password reset link';
    if (isSignUp) return 'Sign up to access your HireNow account';
    return 'Sign in to access your HireNow account';
  };

  // Get form submit handler based on mode
  const getFormHandler = () => {
    if (isForgot) return handleForgotPassword;
    if (isSignUp) return handleSignUp;
    return handleEmailSignIn;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo and Branding */}
        <div className="login-header">
          <div className="logo">
            <svg
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="logo-icon"
              aria-hidden="true"
            >
              <rect width="40" height="40" rx="8" fill="#0066CC" />
              <path
                d="M12 14h6v12h-6zM22 14h6v12h-6zM12 20h16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="logo-text">HireNow</span>
          </div>
          <h1 className="login-title">{getTitle()}</h1>
          <p className="login-subtitle">{getSubtitle()}</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="login-success" role="status">
            <span className="success-icon" aria-hidden="true">✓</span>
            <p className="success-message">{successMessage}</p>
          </div>
        )}

        {/* Error Display */}
        {(error || localError) && (
          <div className="login-error" role="alert">
            <span className="error-icon" aria-hidden="true">
              !
            </span>
            <div className="error-content">
              <p className="error-message">{error?.message || localError}</p>
              <button
                type="button"
                className="error-dismiss"
                onClick={() => {
                  clearError();
                  setLocalError(null);
                }}
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Email/Password Form */}
        <form 
          className="email-login-form" 
          onSubmit={getFormHandler()}
        >
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email address
            </label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isSigningIn}
              autoComplete="email"
            />
          </div>

          {/* Password field - not shown for forgot password */}
          {!isForgot && (
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                required
                disabled={isSigningIn}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                minLength={isSignUp ? 6 : undefined}
              />
            </div>
          )}

          {/* Forgot password link - only for sign in */}
          {!isSignUp && !isForgot && (
            <div className="form-link-row">
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('forgot')}
                disabled={isSigningIn}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-signin"
            disabled={isSigningIn || !email || (!isForgot && !password)}
            aria-busy={isSigningIn}
          >
            {isSigningIn ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                {isForgot ? 'Sending...' : isSignUp ? 'Creating account...' : 'Signing in...'}
              </>
            ) : (
              isForgot ? 'Send reset link' : isSignUp ? 'Create account' : 'Sign in'
            )}
          </button>
        </form>

        {/* Divider - not shown for forgot password */}
        {!isForgot && (
          <div className="login-divider">
            <span>or</span>
          </div>
        )}

        {/* Microsoft Sign-in - not shown for forgot password */}
        {!isForgot && (
          <button
            type="button"
            className="btn btn-secondary btn-signin btn-microsoft"
            onClick={handleMicrosoftSignIn}
            disabled={isSigningIn}
            aria-busy={isSigningIn}
          >
            {isSigningIn ? (
              <>
                <span className="btn-spinner btn-spinner-secondary" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              <>
                <MicrosoftIcon />
                Sign in with Microsoft
              </>
            )}
          </button>
        )}

        {/* Toggle Sign In / Sign Up / Back to sign in */}
        <div className="login-toggle">
          {isForgot ? (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('signin')}
                disabled={isSigningIn}
              >
                Sign in
              </button>
            </p>
          ) : (
            <p>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode(isSignUp ? 'signin' : 'signup')}
                disabled={isSigningIn}
              >
                {isSignUp ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p className="login-help">
            Need help?{' '}
            <a href="mailto:support@hirenow.com" className="help-link">
              Contact Support
            </a>
          </p>
          <p className="login-terms">
            By signing in, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="privacy-link">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Microsoft Icon Component
 * SVG icon for the Microsoft sign-in button
 */
function MicrosoftIcon() {
  return (
    <svg
      className="btn-icon"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
